import { useRef, useState } from 'react';
import { downloadRows, downloadTemplate, exportRowsAsPdf, readSheetRows } from '../lib/excel';
import type { ImportResult } from '../types/models';

/** One column of the import/export sheet: the header shown in Excel, the request field it maps to, and a sample value. */
export type ImportColumn = { header: string; key: string; sample: string };

/** Turn a parsed cell into text. Date cells (from `cellDates`) become a canonical local string:
 *  `YYYY-MM-DD` for a pure date, else `YYYY-MM-DDTHH:mm:ss` — so date/time columns survive as
 *  something callers and the backend can parse unambiguously. */
function cellText(v: unknown): string {
  if (v == null) return '';
  if (v instanceof Date) {
    const p = (n: number) => String(n).padStart(2, '0');
    const date = `${v.getFullYear()}-${p(v.getMonth() + 1)}-${p(v.getDate())}`;
    const h = v.getHours(), m = v.getMinutes(), s = v.getSeconds();
    return (h || m || s) ? `${date}T${p(h)}:${p(m)}:${p(s)}` : date;
  }
  return String(v);
}

/**
 * Import/export toolbar:
 *  - "Excel Format" downloads the import template (headers + one sample row).
 *  - the ⓘ button lists the expected columns.
 *  - "Choose File" + ⬆ uploads a filled sheet; rows are mapped from header text to request
 *    fields and handed to `onImport`. After an import the result is summarised.
 *  - when `exportRows` is supplied, PDF / Excel buttons export the current list.
 */
export function ImportExport({
  columns, templateName, onImport, onDone, disabled,
  exportRows, exportName, exportTitle,
}: {
  columns: ImportColumn[];
  templateName: string;
  onImport: (rows: Record<string, string>[]) => Promise<ImportResult>;
  onDone: () => void;
  disabled?: boolean;
  // Current list to export (objects keyed by display header). Omit to hide the export buttons.
  exportRows?: Record<string, string | number>[];
  exportName?: string;   // base filename for the export, e.g. "staff"
  exportTitle?: string;  // heading shown on the PDF
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [picked, setPicked] = useState<File | null>(null);
  const [infoOpen, setInfoOpen] = useState(false);

  function exportSample() {
    const headers = columns.map(c => c.header);
    const sample: Record<string, string> = {};
    columns.forEach(c => { sample[c.header] = c.sample; });
    downloadTemplate(templateName, headers, sample);
  }

  async function doImport() {
    if (!picked) return;
    setError(null);
    setResult(null);
    setBusy(true);
    try {
      const raw = await readSheetRows(picked);
      const rows = raw.map(r => {
        const o: Record<string, string> = {};
        columns.forEach(c => { o[c.key] = cellText(r[c.header]).trim(); });
        return o;
      });
      const res = await onImport(rows);
      setResult(res);
      setPicked(null);
      if (res.created > 0) onDone();
    } catch (err: any) {
      setError(err?.message ?? 'Import failed.');
    } finally {
      setBusy(false);
    }
  }

  const canExport = !!exportRows && exportRows.length > 0;

  return (
    <>
      <div className="ie-toolbar">
        <button type="button" className="primary ie-format" onClick={exportSample} disabled={disabled}>
          Excel Format ⬇
        </button>
        <button type="button" className="ie-icon-btn ie-info" onClick={() => setInfoOpen(true)}
                title="Expected columns" aria-label="Expected columns">ⓘ</button>

        <label className={`ie-file${disabled ? ' disabled' : ''}`}>
          <span className="ie-file-btn">Choose File</span>
          <span className="ie-file-name">{picked ? picked.name : 'No file chosen'}</span>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            hidden
            disabled={disabled}
            onChange={e => { setPicked(e.target.files?.[0] ?? null); e.target.value = ''; }}
          />
        </label>
        <button type="button" className="ie-icon-btn ie-upload" onClick={doImport}
                disabled={disabled || busy || !picked} title="Upload & import"
                aria-label="Upload and import">{busy ? '…' : '⬆'}</button>

        {exportRows && (
          <>
            <button type="button" className="ie-icon-btn ie-pdf" disabled={!canExport}
                    title="Export PDF" aria-label="Export PDF"
                    onClick={() => exportRowsAsPdf(exportTitle ?? exportName ?? 'Export', exportRows!)}>PDF</button>
            <button type="button" className="ie-icon-btn ie-xls" disabled={!canExport}
                    title="Export Excel" aria-label="Export Excel"
                    onClick={() => downloadRows(`${exportName ?? 'export'}.xlsx`, exportRows!)}>Excel</button>
          </>
        )}
      </div>

      {infoOpen && (
        <div className="modal-backdrop" onMouseDown={() => setInfoOpen(false)}>
          <div className="modal" onMouseDown={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="modal-head">
              <h2>Import columns</h2>
              <button className="modal-close" onClick={() => setInfoOpen(false)} aria-label="Close">✕</button>
            </div>
            <p className="muted" style={{ marginTop: 8 }}>
              Download <strong>Excel Format</strong>, fill rows under these headers, then choose the file and click ⬆.
            </p>
            <div className="card" style={{ marginTop: 8, padding: 0, maxHeight: 320, overflowY: 'auto' }}>
              <table className="data-table">
                <thead><tr><th>Column</th><th>Example</th></tr></thead>
                <tbody>
                  {columns.map(c => (
                    <tr key={c.key}><td style={{ fontWeight: 600 }}>{c.header}</td><td className="muted">{c.sample}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="modal-actions">
              <button type="button" className="primary" onClick={() => setInfoOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {(result || error) && (
        <div className="modal-backdrop" onMouseDown={() => { setResult(null); setError(null); }}>
          <div className="modal" onMouseDown={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className="modal-head">
              <h2>Import result</h2>
              <button className="modal-close" onClick={() => { setResult(null); setError(null); }} aria-label="Close">✕</button>
            </div>

            {error && <div className="error" style={{ marginTop: 12 }}>{error}</div>}

            {result && (
              <div style={{ marginTop: 12 }}>
                <div className="row" style={{ gap: 16 }}>
                  <span style={{ color: 'var(--success, #16a34a)', fontWeight: 600 }}>
                    ✓ {result.created} added
                  </span>
                  {result.failed > 0 && (
                    <span style={{ color: 'var(--danger)', fontWeight: 600 }}>
                      ✕ {result.failed} failed
                    </span>
                  )}
                </div>

                {/* Error list shows only when at least one row failed. */}
                {result.failed > 0 ? (
                  <div className="card" style={{ marginTop: 12, padding: 0, maxHeight: 300, overflowY: 'auto' }}>
                    <table className="data-table">
                      <thead>
                        <tr><th style={{ width: 70 }}>Row</th><th>Error</th></tr>
                      </thead>
                      <tbody>
                        {result.errors.map((e, i) => (
                          <tr key={i}>
                            <td className="slug">{e.row}</td>
                            <td>{e.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="muted" style={{ marginTop: 12 }}>All rows imported successfully.</div>
                )}
              </div>
            )}

            <div className="modal-actions">
              <button type="button" className="primary" onClick={() => { setResult(null); setError(null); }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
